/**
 * 내정보 페이지 (V62)
 *
 * 본인 정보 조회 + 연락처(전화번호/주소) 수정 + 비밀번호 변경
 *
 * - 인적사항: 읽기 전용 (이름, 사번, 이메일, 생년월일, 성별)
 * - 소속/직무: 읽기 전용 (소속, 부서, 직위, 직책)
 * - 연락처: 수정 가능 (전화번호, 주소)
 * - 비밀번호 변경: 모달
 */

import React, { useState } from 'react';
import {
  User, Briefcase, Phone, Key,
  Edit2, Save, X, Eye, EyeOff,
  CheckCircle, Loader,
} from 'lucide-react';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { auth, functions } from '../../firebase/config';

// V62: 전화번호 형식 자동 포맷 (숫자만 추출 → 하이픈 자동 삽입)
function formatPhoneNumber(value: string): string {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }
  if (numbers.length === 10) {
    if (numbers.startsWith('02')) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
    }
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
  if (numbers.length === 9 && numbers.startsWith('02')) {
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5, 9)}`;
  }
  return numbers;
}

function isValidPhoneNumber(value: string): boolean {
  if (!value) return true; // 빈 값 허용
  const numbers = value.replace(/\D/g, '');
  return numbers.length >= 9 && numbers.length <= 11;
}

// ============================================
// 타입
// ============================================
interface UserProfileData {
  nameKo?: string;
  employeeNumber?: string;
  email?: string;
  birthDate?: string;
  gender?: string;
  affiliationName?: string;
  orgUnitName?: string;
  positionTitle?: string;
  jobTitle?: string;
  phoneNumber?: string;
  address?: string;
}

// ============================================
// 메인 컴포넌트
// ============================================
export function MyProfilePage() {
  const { user } = useAuthorization();
  const profile = (user?.profile ?? {}) as UserProfileData;

  return (
    <div style={pageStyle}>
      <h1 style={pageTitleStyle}>내정보</h1>

      {/* 1. 인적사항 (읽기 전용) */}
      <PersonalInfoSection profile={profile} />

      {/* 2. 소속/직무 (읽기 전용) */}
      <OrgInfoSection profile={profile} />

      {/* 3. 연락처 (수정 가능) */}
      <ContactSection profile={profile} />

      {/* 4. 비밀번호 변경 */}
      <PasswordSection />
    </div>
  );
}

// ============================================
// 1. 인적사항 (읽기 전용)
// ============================================
function PersonalInfoSection({ profile }: { profile: UserProfileData }) {
  const genderLabel = profile.gender === 'male' ? '남' : profile.gender === 'female' ? '여' : '-';

  return (
    <div style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <User size={20} style={{ color: 'var(--color-primary)' }} />
        <h3 style={sectionTitleStyle}>인적사항</h3>
      </div>
      <div style={gridStyle}>
        <InfoItem label="이름" value={profile.nameKo || '-'} />
        <InfoItem label="사번" value={profile.employeeNumber || '-'} mono />
        <InfoItem label="이메일" value={profile.email || '-'} />
        <InfoItem label="생년월일" value={profile.birthDate || '-'} />
        <InfoItem label="성별" value={genderLabel} />
      </div>
    </div>
  );
}

// ============================================
// 2. 소속/직무 (읽기 전용)
// ============================================
function OrgInfoSection({ profile }: { profile: UserProfileData }) {
  return (
    <div style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <Briefcase size={20} style={{ color: 'var(--color-primary)' }} />
        <h3 style={sectionTitleStyle}>소속 / 직무</h3>
      </div>
      <div style={gridStyle}>
        <InfoItem label="소속" value={profile.affiliationName || '-'} />
        <InfoItem label="부서" value={profile.orgUnitName || '-'} />
        <InfoItem label="직위" value={profile.positionTitle || '-'} />
        <InfoItem label="직책" value={profile.jobTitle || '-'} />
      </div>
    </div>
  );
}

// ============================================
// 3. 연락처 (수정 가능)
// ============================================
function ContactSection({ profile }: { profile: UserProfileData }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editedData, setEditedData] = useState({
    phoneNumber: profile.phoneNumber || '',
    address: profile.address || '',
  });

  const handleEdit = () => {
    setEditedData({
      phoneNumber: profile.phoneNumber || '',
      address: profile.address || '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData({
      phoneNumber: profile.phoneNumber || '',
      address: profile.address || '',
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!functions) return;
    // V62: 전화번호 형식 검증
    if (editedData.phoneNumber && !isValidPhoneNumber(editedData.phoneNumber)) {
      alert('전화번호 형식이 올바르지 않습니다. (9~11자리 숫자)');
      return;
    }
    setIsLoading(true);
    try {
      const updateMyContactInfoFn = httpsCallable<
        { phoneNumber?: string; address?: string },
        { success: boolean; message: string }
      >(functions, 'updateMyContactInfo');

      const result = await updateMyContactInfoFn({
        phoneNumber: editedData.phoneNumber,
        address: editedData.address,
      });
      if (result.data.success) {
        alert('연락처가 수정되었습니다.');
        setIsEditing(false);
      }
    } catch (err: any) {
      console.error('[updateMyContactInfo] 실패:', err);
      alert(err.message || '연락처 수정에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <Phone size={20} style={{ color: 'var(--color-primary)' }} />
        <h3 style={sectionTitleStyle}>연락처</h3>
        {!isEditing ? (
          <button onClick={handleEdit} style={editBtnStyle}>
            <Edit2 size={16} /> 수정
          </button>
        ) : (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button onClick={handleCancel} style={cancelBtnStyle} disabled={isLoading}>
              <X size={16} /> 취소
            </button>
            <button onClick={handleSave} style={saveBtnStyle} disabled={isLoading}>
              <Save size={16} /> {isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        )}
      </div>
      <div style={gridStyle}>
        <div>
          <div style={labelStyle}>전화번호</div>
          {!isEditing ? (
            <div style={valueStyle}>{profile.phoneNumber || '-'}</div>
          ) : (
            <input
              type="tel"
              value={editedData.phoneNumber}
              onChange={(e) => setEditedData({ ...editedData, phoneNumber: formatPhoneNumber(e.target.value) })}
              style={inputStyle}
              placeholder="전화번호 입력"
            />
          )}
        </div>
        <div>
          <div style={labelStyle}>주소</div>
          {!isEditing ? (
            <div style={valueStyle}>{profile.address || '-'}</div>
          ) : (
            <input
              type="text"
              value={editedData.address}
              onChange={(e) => setEditedData({ ...editedData, address: e.target.value })}
              style={inputStyle}
              placeholder="주소 입력"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 4. 비밀번호 변경
// ============================================
function PasswordSection() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // V277: 한글 제거
  const KOREAN_REGEX = /[ㄱ-ㅎㅏ-ㅣ가-힣]/g;
  const filterKorean = (value: string) => value.replace(KOREAN_REGEX, '');

  const isValid =
    currentPassword.trim().length > 0 &&
    newPassword.trim().length > 0 &&
    !/\s/.test(newPassword) &&
    !KOREAN_REGEX.test(newPassword) &&
    newPassword.length >= 8 &&
    newPassword !== 'ngle1234' &&
    newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword.trim()) {
      setError('현재 비밀번호를 입력해주세요.');
      return;
    }
    if (!newPassword || newPassword.trim().length === 0) {
      setError('새 비밀번호를 입력해주세요.');
      return;
    }
    if (/\s/.test(newPassword)) {
      setError('비밀번호에 공백을 포함할 수 없습니다.');
      return;
    }
    if (KOREAN_REGEX.test(newPassword)) {
      setError('비밀번호에는 한글을 사용할 수 없습니다.');
      return;
    }
    if (newPassword.length < 8) {
      setError('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }
    if (newPassword === 'ngle1234') {
      setError('초기 비밀번호와 다른 비밀번호를 사용해주세요.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    setSubmitting(true);
    try {
      if (!functions || !auth) {
        setError('Firebase가 초기화되지 않았습니다.');
        return;
      }

      // 1. 현재 비밀번호 검증 (Firebase Auth reauthenticate)
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || !firebaseUser.email) {
        setError('로그인 정보를 확인할 수 없습니다.');
        return;
      }

      try {
        const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
        await reauthenticateWithCredential(firebaseUser, credential);
      } catch {
        setError('현재 비밀번호가 올바르지 않습니다.');
        setSubmitting(false);
        return;
      }

      // 2. 비밀번호 변경 CF 호출
      const changeMyPasswordFn = httpsCallable<
        { newPassword: string },
        { success: boolean }
      >(functions, 'changeMyPassword');

      await changeMyPasswordFn({ newPassword });

      setSuccess('비밀번호가 변경되었습니다.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      console.error('비밀번호 변경 실패:', err);
      setError(err.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <button onClick={() => setIsOpen(true)} style={pwChangeBtnStyle}>
        <Key size={16} /> 비밀번호 변경
      </button>

      {/* 비밀번호 변경 모달 */}
      {isOpen && (
        <div style={modalOverlayStyle} onClick={handleClose}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>비밀번호 변경</h3>
              <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* 현재 비밀번호 */}
              <div style={fieldStyle}>
                <label style={fieldLabelStyle}>현재 비밀번호</label>
                <div style={inputWrapperStyle}>
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="현재 비밀번호 입력"
                    style={pwInputStyle}
                    autoComplete="current-password"
                  />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)} style={eyeBtnStyle}>
                    {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* 새 비밀번호 */}
              <div style={fieldStyle}>
                <label style={fieldLabelStyle}>새 비밀번호</label>
                <div style={inputWrapperStyle}>
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(filterKorean(e.target.value))}
                    placeholder="8자 이상 입력"
                    style={pwInputStyle}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowNew(!showNew)} style={eyeBtnStyle}>
                    {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {newPassword.length > 0 && newPassword.length < 8 && (
                  <p style={hintStyle}>{8 - newPassword.length}자 더 입력해주세요</p>
                )}
              </div>

              {/* 새 비밀번호 확인 */}
              <div style={fieldStyle}>
                <label style={fieldLabelStyle}>새 비밀번호 확인</label>
                <div style={inputWrapperStyle}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(filterKorean(e.target.value))}
                    placeholder="비밀번호 재입력"
                    style={pwInputStyle}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={eyeBtnStyle}>
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {confirmPassword.length > 0 && (
                  <p style={{ ...hintStyle, color: newPassword === confirmPassword ? '#10b981' : '#ef4444' }}>
                    {newPassword === confirmPassword ? '비밀번호가 일치합니다' : '비밀번호가 일치하지 않습니다'}
                  </p>
                )}
              </div>

              {/* 에러/성공 메시지 */}
              {error && <p style={errorMsgStyle}>{error}</p>}
              {success && <p style={successMsgStyle}>{success}</p>}

              {/* 제출 */}
              <button
                type="submit"
                disabled={!isValid || submitting}
                style={isValid && !submitting ? submitBtnStyle : disabledBtnStyle}
              >
                {submitting ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={18} />}
                {submitting ? '변경 중...' : '비밀번호 변경'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// 공통 컴포넌트
// ============================================
function InfoItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={{ ...valueStyle, ...(mono ? { fontFamily: 'monospace', fontSize: '13px' } : {}) }}>
        {value}
      </div>
    </div>
  );
}

// ============================================
// 스타일
// ============================================
const pageStyle: React.CSSProperties = {
  padding: '24px',
  maxWidth: '800px',
  margin: '0 auto',
};

const pageTitleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: '700',
  color: 'var(--color-gray-800)',
  marginBottom: '24px',
};

const sectionStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  marginBottom: '16px',
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '1px solid var(--color-gray-200)',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '16px',
  fontWeight: '600',
  color: 'var(--color-gray-800)',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: '600',
  color: 'var(--color-gray-500)',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const valueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-gray-800)',
  fontWeight: '500',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  outline: 'none',
  boxSizing: 'border-box',
};

const editBtnStyle: React.CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: '500',
  color: 'var(--color-primary)',
  background: 'var(--color-primary-light, #e8f4ff)',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const pwChangeBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 20px',
  fontSize: '14px',
  fontWeight: '500',
  color: '#fff',
  background: '#3b82f6',
  border: '1px solid #3b82f6',
  borderRadius: '8px',
  cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 12px',
  fontSize: '13px',
  color: 'var(--color-gray-600)',
  background: 'var(--color-gray-100)',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const saveBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: '600',
  color: 'white',
  background: 'var(--color-primary)',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

// 비밀번호 모달 스타일
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  padding: '32px',
  width: '400px',
  maxWidth: '90vw',
  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
};

const fieldStyle: React.CSSProperties = {
  marginBottom: '16px',
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px',
};

const inputWrapperStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const pwInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 40px 10px 12px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  outline: 'none',
  boxSizing: 'border-box',
};

const eyeBtnStyle: React.CSSProperties = {
  position: 'absolute',
  right: '10px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#9ca3af',
  padding: '4px',
};

const hintStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6b7280',
  marginTop: '4px',
};

const errorMsgStyle: React.CSSProperties = {
  color: '#dc2626',
  fontSize: '14px',
  marginBottom: '12px',
};

const successMsgStyle: React.CSSProperties = {
  color: '#10b981',
  fontSize: '14px',
  marginBottom: '12px',
};

const submitBtnStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px',
  fontSize: '15px',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#3b82f6',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const disabledBtnStyle: React.CSSProperties = {
  ...submitBtnStyle,
  backgroundColor: '#9ca3af',
  cursor: 'not-allowed',
};
